import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '../db/local.js';
import { pendingCount } from '../sync/engine.js';
import { ApiError } from '../api.js';
import { useChildCodeLogin, useLogin, usePasskeyLogin } from '../auth.js';
import { isCancelled, passkeysSupported } from '../passkeys.js';
import { PasswordInput } from './PasswordInput.js';

export function LoginForm(): React.JSX.Element {
  const { t } = useTranslation();
  const login = useLogin();
  const passkey = usePasskeyLogin();
  const [canUsePasskey] = useState(passkeysSupported);
  const emailId = useId();
  const passwordId = useId();
  const errorId = useId();

  // Liegt noch etwas in der Outbox, ist die Sitzung abgelaufen, nicht das
  // Konto weg — das soll man wissen, bevor man sich Sorgen macht.
  const waiting = useLiveQuery(() => pendingCount(db), [], 0);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  // „Passwort falsch" nur, wenn der Server es sagt. Ohne Antwort (Funkloch,
  // Server weg) ist es eine Verbindungsfrage — sonst tippt man das richtige
  // Passwort immer wieder neu.
  const errorMessage = login.isError
    ? !(login.error instanceof ApiError)
      ? t('login.offline')
      : login.error.status === 429
        ? t('login.tooMany')
        : login.error.status >= 500
          ? t('login.serverError')
          : t('login.failed')
    : passkey.isError && !isCancelled(passkey.error)
      ? passkey.error instanceof ApiError && passkey.error.status === 429
        ? t('login.tooMany')
        : passkey.error instanceof ApiError && passkey.error.status === 401
          ? t('login.passkeyFailed')
          : t('login.serverError')
      : null;
  const busy = login.isPending || passkey.isPending;

  return (
    <main className="mx-auto flex min-h-dvh w-full max-w-sm flex-col justify-center gap-6 px-4">
      <div>
        <h1 className="font-display text-4xl font-bold tracking-wide uppercase">{t('app.name')}</h1>
        <p className="mt-1 text-sm text-ink-muted">{t('login.intro')}</p>
      </div>

      {waiting > 0 ? (
        <p role="status" className="kw-tile border-warn text-sm">
          {t('login.expired', { count: waiting })}
        </p>
      ) : null}

      <form
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          passkey.reset();
          login.mutate({ email, password });
        }}
      >
        <div className="flex flex-col gap-1">
          <label htmlFor={emailId} className="kw-label">
            {t('login.email')}
          </label>
          <input
            id={emailId}
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
            className="kw-field"
          />
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor={passwordId} className="kw-label">
            {t('login.password')}
          </label>
          <PasswordInput
            id={passwordId}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
            autoComplete="current-password"
            // Hilft Passwortverwaltern und dem iOS-Schlüsselbund.
            className="kw-field"
            aria-describedby={errorMessage ? errorId : undefined}
          />
        </div>

        {errorMessage ? (
          <p id={errorId} role="alert" className="text-sm text-danger">
            {errorMessage}
          </p>
        ) : null}

        <button type="submit" disabled={busy} className="kw-btn w-full">
          {login.isPending ? t('login.submitting') : t('login.submit')}
        </button>
      </form>

      {canUsePasskey ? (
        // Außerhalb des Formulars: E-Mail und Passwort braucht es dafür nicht,
        // und `required` darf den Knopf nicht blockieren.
        <button
          type="button"
          disabled={busy}
          onClick={() => {
            login.reset();
            passkey.mutate();
          }}
          className="kw-btn kw-btn-quiet w-full"
        >
          {passkey.isPending ? t('login.passkeyBusy') : t('login.passkey')}
        </button>
      ) : null}

      <ChildCodeLogin />

      <p className="text-xs text-ink-muted">{t('login.closedRegistration')}</p>
    </main>
  );
}

/**
 * Kinder (E16) melden sich mit dem Code an, den ein Erwachsener auf seinem
 * Telefon erzeugt — kein Passwort, keine E-Mail. Aufgeklappt erst auf Tipp:
 * Für alle anderen ist es nur eine Zeile.
 */
function ChildCodeLogin(): React.JSX.Element {
  const { t } = useTranslation();
  const codeId = useId();
  const errorId = useId();
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState('');
  const login = useChildCodeLogin();

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="self-start py-2 text-sm text-ink-muted underline underline-offset-2"
      >
        {t('login.childOpen')}
      </button>
    );
  }

  const error = login.isError
    ? !(login.error instanceof ApiError)
      ? t('login.offline')
      : login.error.status === 429
        ? t('login.tooMany')
        : t('login.childFailed')
    : null;

  return (
    <form
      className="kw-tile flex flex-col gap-2"
      onSubmit={(event) => {
        event.preventDefault();
        login.mutate(code);
      }}
    >
      <label htmlFor={codeId} className="kw-label">
        {t('login.childCode')}
      </label>
      <p className="text-xs text-ink-muted">{t('login.childHint')}</p>
      <input
        id={codeId}
        value={code}
        onChange={(event) => setCode(event.target.value)}
        required
        autoCapitalize="characters"
        autoComplete="one-time-code"
        spellCheck={false}
        aria-describedby={error ? errorId : undefined}
        className="kw-field font-display text-xl tracking-widest uppercase"
      />
      {error ? (
        <p id={errorId} role="alert" className="text-sm text-danger">
          {error}
        </p>
      ) : null}
      <button type="submit" disabled={login.isPending || !code.trim()} className="kw-btn">
        {t('login.childSubmit')}
      </button>
    </form>
  );
}
